import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatDateLong, formatTime12h } from "@/lib/api";
import { Loader2, CheckCircle2, PenLine, ArrowLeft } from "lucide-react";

const MAX = 2000;

const Reflect = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("session");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // { session_id: text }
  const [saving, setSaving] = useState(null); // session_id being saved
  const [savedAt, setSavedAt] = useState({}); // { session_id: iso }

  useEffect(() => {
    api
      .get(`/reflect/${token}`)
      .then((r) => {
        setInfo(r.data);
        // Seed drafts from existing reflections
        const d = {};
        (r.data.sessions || []).forEach((s) => {
          if (s.reflection_text) d[s.id] = s.reflection_text;
        });
        setDrafts(d);
      })
      .catch(() => setInfo(false))
      .finally(() => setLoading(false));
  }, [token]);

  const pastSessions = useMemo(
    () => (info?.sessions || []).filter((s) => s.is_past),
    [info]
  );
  const upcomingSessions = useMemo(
    () => (info?.sessions || []).filter((s) => !s.is_past),
    [info]
  );

  const submit = async (sessionId) => {
    const text = (drafts[sessionId] || "").trim();
    if (!text) {
      toast.error("Type something first — even one sentence counts.");
      return;
    }
    setSaving(sessionId);
    try {
      const r = await api.post(`/reflect/${token}`, { session_id: sessionId, text });
      setSavedAt((prev) => ({ ...prev, [sessionId]: r.data.reflection_submitted_at }));
      toast.success("Reflection saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save reflection");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="reflect-loading">
        <Loader2 className="animate-spin text-[#4F5D4F]" size={28} />
      </main>
    );
  }

  if (info === false || !info) {
    return (
      <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24" data-testid="reflect-invalid">
        <div className="max-w-md mx-auto px-6 text-center">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-4">Link not found</p>
          <h1 className="font-serif text-4xl text-[#4F5D4F] leading-tight mb-6">This reflection link isn't active.</h1>
          <p className="text-[#4F5D4F]/80 font-light mb-8">
            The link may have expired or been mistyped. Reach out and we'll get you a fresh one.
          </p>
          <Link to="/" className="inline-block bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-8 py-4 text-xs tracking-eyebrow uppercase" data-testid="reflect-return-home">
            Return home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24" data-testid="reflect-page">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">A private space · Just between us</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#4F5D4F] leading-tight mb-4" data-testid="reflect-greeting">
          Hello, <em className="text-[#B89B63]">{info.client?.name?.split(" ")[0] || "friend"}</em>.
        </h1>
        <p className="text-[#4F5D4F]/85 font-light text-lg leading-relaxed mb-12 max-w-2xl">
          Drop a few words after each session — a feeling, a question, a small win. I'll read every one before we sit down again, so we can pick up right where you left off.
        </p>

        {pastSessions.length === 0 ? (
          <div className="bg-white border border-[#D7B2A3]/50 p-10 text-center" data-testid="reflect-no-past">
            <PenLine className="mx-auto text-[#B89B63] mb-4" size={28} />
            <p className="font-serif text-2xl text-[#4F5D4F] mb-2">Nothing to reflect on yet.</p>
            <p className="text-[#8C9A88] font-light">
              This page will bloom after your first session together. Come back once you've had time to sit with what came up.
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="reflect-past-list">
            {pastSessions.map((s) => {
              const isFocused = focusId === s.id;
              const text = drafts[s.id] || "";
              const savedIso = savedAt[s.id] || s.reflection_submitted_at;
              const hasSaved = !!savedIso;
              return (
                <section
                  key={s.id}
                  className={`bg-white border p-8 ${isFocused ? "border-[#B89B63] shadow-sm" : "border-[#D7B2A3]/50"}`}
                  data-testid={`reflect-session-${s.id}`}
                >
                  <div className="flex items-baseline justify-between gap-4 flex-wrap mb-5">
                    <div>
                      <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-1">Session</p>
                      <h2 className="font-serif text-2xl text-[#4F5D4F]">{formatDateLong(s.session_date)}</h2>
                      <p className="text-sm text-[#8C9A88] mt-1">{formatTime12h(s.session_time)} ET</p>
                    </div>
                    {hasSaved && (
                      <span className="inline-flex items-center gap-2 text-xs tracking-eyebrow uppercase text-[#B89B63]" data-testid={`reflect-saved-${s.id}`}>
                        <CheckCircle2 size={14} /> Saved
                      </span>
                    )}
                  </div>
                  <textarea
                    value={text}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [s.id]: e.target.value.slice(0, MAX) }))}
                    placeholder="What surfaced today? A word, a feeling, a small win…"
                    className="w-full min-h-[140px] rounded-none border border-[#D7B2A3] bg-[#F6F1EA]/60 p-4 text-[#4F5D4F] font-light leading-relaxed focus:outline-none focus:border-[#4F5D4F] focus:bg-white"
                    data-testid={`reflect-textarea-${s.id}`}
                  />
                  <div className="flex items-center justify-between mt-3 gap-4">
                    <span className="text-xs text-[#8C9A88]">{text.length} / {MAX}</span>
                    <button
                      onClick={() => submit(s.id)}
                      disabled={saving === s.id || !text.trim()}
                      className="bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 text-xs tracking-eyebrow uppercase inline-flex items-center gap-2"
                      data-testid={`reflect-save-${s.id}`}
                    >
                      {saving === s.id ? <><Loader2 className="animate-spin" size={14}/> Saving…</> : hasSaved ? "Update reflection" : "Save reflection"}
                    </button>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {upcomingSessions.length > 0 && (
          <div className="mt-12 bg-[#4F5D4F]/5 border border-[#D7B2A3]/40 p-6" data-testid="reflect-upcoming">
            <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-2">Coming up</p>
            <ul className="text-[#4F5D4F] font-light space-y-1">
              {upcomingSessions.map((s) => (
                <li key={s.id}>
                  {formatDateLong(s.session_date)} · {formatTime12h(s.session_time)} ET
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-16 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-eyebrow uppercase text-[#4F5D4F] hover:text-[#B89B63]" data-testid="reflect-back-home">
            <ArrowLeft size={14} /> Return home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Reflect;
